import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, lastValueFrom, take } from 'rxjs';
import { Globals } from '../../globals';
import { equalsCaseInsensitive } from '../../helpers';
import { EnergyDashboardConfigItem } from '../../interfaces/energydashboardconfig.interface';
import { Device } from '../devices/devices.interfaces';
import { Functionblock } from '../functionblocks/functionblocks.interfaces';
import { UserSettings } from '../users/users.interfaces';
import {
  LabelKeyValue,
  LuqasBoxChSubscriptionComparisonConfig,
  LuqasBoxEmsSubscriptionComparisonConfig,
  LuqasHubSubscriptionComparisonConfig,
  LuqasSubscriptionComparisonConfig,
  Settings,
  SettingsEnergyDashboard,
  SubscriptionComparisonConfig,
} from './settings.interfaces';

@Injectable({
  providedIn: 'root',
})

/**
 * Settings REST calls
 */
export class SettingsService {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);

  defaultVersion = '0.0.0';
  ls_dashboardConfig = 'dashboardConfig';
  ls_functionblockConfig = 'functionblockConfig';
  ls_functionblockStatusConfig = 'functionblockStatusConfig';
  ls_energyDashboardConfig = 'energyDashboardConfig';
  ls_userSettings = 'userSettings';
  ls_luqasSubscriptionComparison = 'luqasSubscriptionComparison';
  ls_luqasHubSubscriptionComparison = 'luqasHubSubscriptionComparison';
  ls_luqasBoxEvSubscriptionComparison = 'luqasBoxEvSubscriptionComparison';
  ls_luqasBoxEmsSubscriptionComparison = 'luqasBoxEmsSubscriptionComparison';
  ls_luqasBoxChSubscriptionComparison = 'luqasBoxChSubscriptionComparison';

  /**
   * Ping the server
   * Returns true when the server and the ms sql server are availlable, returns false when the ms sql is not availlable
   */
  pingServer(): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtSettings/ping`;
    return this.http.get<boolean>(url);
  }

  /**
   * Get the dashboardconfig for a type of functionblock
   * @param fbType Functionblock type
   */
  getDashboardConfigByFBTypeAndVariant(
    fbType: string,
    fbVariant: string,
  ): object | null {
    const dashboardConfigLocalStorage =
      localStorage.getItem(this.ls_dashboardConfig) || null;
    const config = dashboardConfigLocalStorage
      ? JSON.parse(dashboardConfigLocalStorage)
      : null;
    if (fbVariant) {
      return config?.config.filter(
        (x: any) =>
          x.type.toLowerCase() === fbType.toLowerCase() &&
          x.variant &&
          x.variant.indexOf(fbVariant) > -1,
      )[0];
    }
    return config?.config.filter(
      (x: any) => x.type.toLowerCase() === fbType.toLowerCase(),
    )[0];
  }

  /**
   * Get the functionblock config for a type of functionblock
   * @param fbType Functionblock type
   */
  getFunctionBlockConfigByFBType(fbType: string, fbVariant = ''): any {
    const config = JSON.parse(
      localStorage.getItem(this.ls_functionblockConfig) || 'null',
    );
    if (fbVariant) {
      return config.config.filter(
        (x: any) =>
          x.type.toLowerCase() === fbType.toLowerCase() &&
          x.variant &&
          x.variant.indexOf(fbVariant) > -1 &&
          !x.takeIfFbHasProperties,
      )[0];
    }
    return config.config.filter(
      (x: any) => x.type.toLowerCase() === fbType.toLowerCase(),
    )[0];
  }

  private getFunctionBlockChartConfigByTypeAndProperties(
    fbTypeName: string,
    propertyNames: string[],
  ): any | undefined {
    const chartConfigsWrapperJson = localStorage.getItem(
      this.ls_functionblockConfig,
    );

    if (chartConfigsWrapperJson) {
      const chartConfigsWrapper: Settings = JSON.parse(chartConfigsWrapperJson);
      const chartConfigs = chartConfigsWrapper.config;

      let matchedPropertyFromConfig: string | undefined = undefined;

      const matchedChartConfig = chartConfigs.find((chartConfig: any) => {
        // if type matches and if takeIfFbHasProperties is present
        if (
          equalsCaseInsensitive(chartConfig.type, fbTypeName) &&
          chartConfig.takeIfFbHasProperties
        ) {
          // check if takeIfFbHasProperties contains any of the properties of the function block
          return (chartConfig.takeIfFbHasProperties as string[]).some(
            (propertyFromConfig) =>
              propertyNames.some((fbProperty) => {
                const isMatch = equalsCaseInsensitive(
                  propertyFromConfig,
                  fbProperty,
                );
                if (isMatch) {
                  matchedPropertyFromConfig = propertyFromConfig;
                  return true;
                }
                return false;
              }),
          );
        }
        return undefined;
      });

      if (matchedChartConfig) {
        // Replace the properties on the request config that have the same name
        // as the first item in takeIfFbHasProperties with the matched item in the array.
        if (matchedPropertyFromConfig !== undefined) {
          const graphsConfig = matchedChartConfig?.['layout']?.['graph'];

          if (graphsConfig) {
            const propertyToReplace =
              matchedChartConfig.propertyPlaceholder ??
              (matchedChartConfig.takeIfFbHasProperties as string[])[0];

            if (propertyToReplace !== matchedPropertyFromConfig) {
              const replacePropertyOnRequestConfig = (
                graphPeriodConfig:
                  | { request?: { properties?: { property?: string }[] } }
                  | undefined,
              ) => {
                graphPeriodConfig?.request?.properties?.forEach(
                  (propConfig) => {
                    if (
                      equalsCaseInsensitive(
                        propConfig.property,
                        propertyToReplace,
                      )
                    ) {
                      propConfig.property = matchedPropertyFromConfig;
                    }
                  },
                );
              };

              replacePropertyOnRequestConfig(graphsConfig['day']);
              replacePropertyOnRequestConfig(graphsConfig['week']);
              replacePropertyOnRequestConfig(graphsConfig['month']);
            }
          }
        }

        return matchedChartConfig;
      }

      console.error('No matching functionblock chart config found.');
      return undefined;
    }

    console.error('No function block charts config found in local storage.');
    return undefined;
  }

  getFunctionBlockChartConfig(functionBlock: Functionblock): any | undefined {
    const variant = functionBlock.variant || '';
    const typeName = functionBlock.type?.name || '';

    const config = this.getFunctionBlockConfigByFBType(
      typeName.toLowerCase(),
      variant.toLowerCase(),
    );

    if (config) return config;

    // todo: make sure properties is set before entering this method

    const properties = functionBlock?.configuration?.properties;

    if (properties) {
      const propertyNames = Object.keys(properties);
      const result = this.getFunctionBlockChartConfigByTypeAndProperties(
        typeName,
        propertyNames,
      );

      if (result) {
        return result;
      }

      console.error('No matching function block config found.');
      return undefined;
    }

    console.error('No configuration with properties found on function block.');
    return undefined;
  }

  /**
   * Get the functionblocks statusses config for a type of functionblock
   * @param fbType Functionblock type
   */
  getFunctionBlockStatusConfigByFBType(fbType: string, fbVariant = ''): string {
    const config = JSON.parse(
      localStorage.getItem(this.ls_functionblockStatusConfig) || 'null',
    );
    if (fbVariant) {
      return config.config.find(
        (item: any) =>
          (item.type.toLowerCase() === fbType.toLowerCase() ||
            (item.dynamicType &&
              item.dynamicType.toLowerCase() === fbType.toLowerCase())) &&
          item.variant &&
          item.variant.indexOf(fbVariant) > -1,
      );
    }
    return config.config.find(
      (item: any) =>
        item.type.toLowerCase() === fbType.toLowerCase() ||
        (item.dynamicType &&
          item.dynamicType.toLowerCase() === fbType.toLowerCase()),
    );
  }

  /**
   * Get the entire energy dashboard config
   */
  getCompleteEnergyDashboardConfig(): EnergyDashboardConfigItem[] {
    const config: SettingsEnergyDashboard = JSON.parse(
      localStorage.getItem(this.ls_energyDashboardConfig) || 'null',
    );
    return config.config;
  }

  /**
   * Get the filter of functionblocks to subscribe to mqtt/to set filter
   */
  getFilter(): Observable<Device[]> {
    const url = `${this.globals.apiRoot}/jwtSettings/filter`;
    return this.http.get<Device[]>(url);
  }

  /**
   * Update dashboard config to the latest json
   */
  updateDashboardConfig(): void {
    const config = JSON.parse(
      localStorage.getItem(this.ls_dashboardConfig) || 'null',
    );
    const config_version =
      config !== null ? config.version : this.defaultVersion;

    this.getDashboardConfig(config_version)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if (data !== null) {
            // only save config when we received new data
            localStorage.setItem(this.ls_dashboardConfig, JSON.stringify(data));
          }
        },
        error: (error) => console.warn('CONFIG - error', error),
      });
  }

  /**
   * Update dashboard config to the latest json
   */
  updateFunctionBlockConfig(): void {
    const config = JSON.parse(
      localStorage.getItem(this.ls_functionblockConfig) || 'null',
    );
    const config_version =
      config !== null ? config.version : this.defaultVersion;

    this.getFunctionBlockConfig(config_version).subscribe((data) => {
      if (data !== null) {
        // only save config when we received new data
        localStorage.setItem(this.ls_functionblockConfig, JSON.stringify(data));
      }
    });
  }

  /**
   * Update the functionblocks statusses config to the latest json
   */
  updateFunctionBlockStatusConfig(): void {
    const config = JSON.parse(
      localStorage.getItem(this.ls_functionblockStatusConfig) || 'null',
    );
    const config_version =
      config !== null ? config.version : this.defaultVersion;

    this.getFunctionBlockStatusConfig(config_version).subscribe((data) => {
      if (data !== null) {
        // only save config when we received new data
        localStorage.setItem(
          this.ls_functionblockStatusConfig,
          JSON.stringify(data),
        );
      }
    });
  }

  /**
   * Update the energy dashboard config to the latest json
   */
  updateEnergyDashboardConfig(): void {
    const config = JSON.parse(
      localStorage.getItem(this.ls_energyDashboardConfig) || 'null',
    );
    const config_version =
      config !== null ? config.version : this.defaultVersion;

    this.getEnergyDashboardConfig(config_version).subscribe((data) => {
      if (data !== null) {
        localStorage.setItem(
          this.ls_energyDashboardConfig,
          JSON.stringify(data),
        );
      }
    });
  }
  private async updateComparisonConfig<T extends SubscriptionComparisonConfig>({
    localStorageKey,
    getConfigObservable,
  }: {
    localStorageKey: string;
    getConfigObservable: (version: string) => Observable<Settings<T>>;
  }): Promise<T | null> {
    const settingsFromLocalStorage = JSON.parse(
      localStorage.getItem(localStorageKey) || 'null',
    );
    const version = settingsFromLocalStorage
      ? settingsFromLocalStorage.version
      : this.defaultVersion;

    let settingsFromBackend: Settings<T> | undefined = undefined;

    try {
      settingsFromBackend = await lastValueFrom(getConfigObservable(version));
    } catch (error) {
      console.error(
        `updateComparisonConfig failed (localStorageKey: ${localStorageKey}).`,
      );
      console.error(error);
    }

    if (settingsFromBackend?.config) {
      localStorage.setItem(
        localStorageKey,
        JSON.stringify(settingsFromBackend),
      );
      return settingsFromBackend.config;
    } else if (settingsFromLocalStorage?.config) {
      return settingsFromLocalStorage.config;
    }

    return null;
  }

  async updateLuqasSubscriptionComparisonConfig(): Promise<LuqasSubscriptionComparisonConfig | null> {
    return await this.updateComparisonConfig<LuqasSubscriptionComparisonConfig>(
      {
        localStorageKey: this.ls_luqasSubscriptionComparison,
        getConfigObservable: (version) =>
          this.getLuqasSubscriptionComparisonConfig(version),
      },
    );
  }

  async updateLuqasBoxEmsSubscriptionComparisonConfig(): Promise<LuqasBoxEmsSubscriptionComparisonConfig | null> {
    return await this.updateComparisonConfig<LuqasBoxEmsSubscriptionComparisonConfig>(
      {
        localStorageKey: this.ls_luqasBoxEmsSubscriptionComparison,
        getConfigObservable: (version) =>
          this.getLuqasBoxEmsSubscriptionComparisonConfig(version),
      },
    );
  }

  async updateLuqasBoxChSubscriptionComparisonConfig(): Promise<LuqasBoxChSubscriptionComparisonConfig | null> {
    return await this.updateComparisonConfig<LuqasBoxChSubscriptionComparisonConfig>(
      {
        localStorageKey: this.ls_luqasBoxChSubscriptionComparison,
        getConfigObservable: (version) =>
          this.getLuqasBoxChSubscriptionComparisonConfig(version),
      },
    );
  }

  async updateLuqasHubSubscriptionComparisonConfig(): Promise<LuqasHubSubscriptionComparisonConfig | null> {
    return await this.updateComparisonConfig<LuqasHubSubscriptionComparisonConfig>(
      {
        localStorageKey: this.ls_luqasHubSubscriptionComparison,
        getConfigObservable: (version) =>
          this.getLuqasHubSubscriptionComparisonConfig(version),
      },
    );
  }

  /**
   * Get the userSettings from local storage
   * @returns the userSettings from local storage or undefined
   */
  getUserSettings(): UserSettings | undefined {
    const user = JSON.parse(
      localStorage.getItem(this.globals.localStorageTokenName) || 'null',
    );
    return user?.[this.ls_userSettings];
  }

  /**
   * Save the userSettings in local storage
   * @param userSettings the userSettings to save to local storage
   */
  setUserSettings(userSettings: UserSettings) {
    const user =
      JSON.parse(
        localStorage.getItem(this.globals.localStorageTokenName) || 'null',
      ) ?? {};
    user[this.ls_userSettings] = userSettings;
    localStorage.setItem(
      this.globals.localStorageTokenName,
      JSON.stringify(user),
    );
  }

  getSatelArmModes(): Observable<LabelKeyValue[]> {
    const url = `${this.globals.apiRoot}/jwtSettings/satel-arm-modes`;
    return this.http.get<LabelKeyValue[]>(url);
  }

  /**
   * Get entire dashboard configuration
   */
  private getDashboardConfig(version: string): Observable<Settings> {
    const url = `${this.globals.apiRoot}/jwtSettings/dashboardConfig/${version}`;
    return this.http.get<Settings>(url);
  }

  /**
   * Get entire functionblock configuration
   */
  private getFunctionBlockConfig(version: string): Observable<Settings> {
    // //#region test code, to remove
    // return of(functionBlockConfig);
    // //#endregion
    const url = `${this.globals.apiRoot}/jwtSettings/functionblockConfig/${version}`;
    return this.http.get<Settings>(url);
  }

  /**
   * Get entire functionblock status configuration
   */
  private getFunctionBlockStatusConfig(version: string): Observable<Settings> {
    const url = `${this.globals.apiRoot}/jwtSettings/functionblockStatusConfig/${version}`;
    return this.http.get<Settings>(url);
  }

  /**
   * Get entire energy dashboard status configuration
   */
  private getEnergyDashboardConfig(version: string): Observable<Settings> {
    // //#region test code, to remove
    // return of(energyDashboardConfigDummy);
    // //#endregion

    const url = `${this.globals.apiRoot}/jwtSettings/energydashboardconfig/${version}`;
    return this.http.get<Settings>(url);
  }

  private getLuqasSubscriptionComparisonConfig(
    version: string,
  ): Observable<Settings<LuqasSubscriptionComparisonConfig>> {
    const url = `${this.globals.apiRoot}/jwtSettings/luqasSubscriptionComparison/${version}`;
    return this.http.get<Settings<LuqasSubscriptionComparisonConfig>>(url);
  }

  private getLuqasHubSubscriptionComparisonConfig(
    version: string,
  ): Observable<Settings<LuqasHubSubscriptionComparisonConfig>> {
    const url = `${this.globals.apiRoot}/jwtSettings/luqasHubSubscriptionComparison/${version}`;
    return this.http.get<Settings<LuqasHubSubscriptionComparisonConfig>>(url);
  }

  private getLuqasBoxEmsSubscriptionComparisonConfig(
    version: string,
  ): Observable<Settings<LuqasBoxEmsSubscriptionComparisonConfig>> {
    const url = `${this.globals.apiRoot}/jwtSettings/luqasBoxEmsComparisonConfig/${version}`;
    return this.http.get<Settings<LuqasBoxEmsSubscriptionComparisonConfig>>(
      url,
    );
  }

  private getLuqasBoxChSubscriptionComparisonConfig(
    version: string,
  ): Observable<Settings<LuqasBoxChSubscriptionComparisonConfig>> {
    const url = `${this.globals.apiRoot}/jwtSettings/luqasBoxChComparisonConfig/${version}`;
    return this.http.get<Settings<LuqasBoxChSubscriptionComparisonConfig>>(url);
  }
}
